// Per-tool input persistence. Keeps a user's last typed values across
// page reloads inside localStorage. Each tool calls `save` on change
// (debouncing in the component) and `load` in the constructor.
//
// `validate` is a per-tool predicate — restoration is skipped if the
// stored value fails the predicate (e.g. malformed JSON). The component
// then renders its default state instead of crashing on restore.
//
// Keys are namespaced `xc:input:<slug>` to avoid colliding with other
// localStorage usage on the domain.

const KEY_PREFIX = 'xc:input:';

const hasStorage = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        const probe = '__xc_storage_probe__';
        window.localStorage.setItem(probe, probe);
        window.localStorage.removeItem(probe);
        return true;
    } catch {
        return false;
    }
};

export const PersistedInput = {
    load<T>(slug: string, fallback: T, validate?: (v: unknown) => boolean): T {
        if (!hasStorage()) return fallback;
        try {
            const raw = window.localStorage.getItem(KEY_PREFIX + slug);
            if (raw === null) return fallback;
            const parsed = JSON.parse(raw);
            if (validate && !validate(parsed)) return fallback;
            return parsed as T;
        } catch {
            return fallback;
        }
    },

    save(slug: string, value: unknown): void {
        if (!hasStorage()) return;
        try {
            window.localStorage.setItem(KEY_PREFIX + slug, JSON.stringify(value));
        } catch {
            // Quota exceeded or storage disabled — silently ignore.
            // Persisted inputs are a convenience, never load-bearing.
        }
    },

    clear(slug: string): void {
        if (!hasStorage()) return;
        try {
            window.localStorage.removeItem(KEY_PREFIX + slug);
        } catch {
            // ignore
        }
    },
};
